import { useQuery } from "@tanstack/react-query";
import { ViewBooking } from "collins-js-sdk";
import { MapPin, User } from "lucide-react";
import React, { useEffect, useState } from "react";
import { getAreaAvailability } from "../../../../../../APIs/Booking/GetAreaAvailability";
import { useDeviceContext } from "../../../../../../context/DeviceContext";
import { useDiaryContext } from "../../../../../../hooks/useDiaryContext";
import { WaitlistSlot } from "../../../../../../types/areaAvailability";
import { BookableArea } from "../../../../../../types/venues";
import Loading from "../../../../../../utils/Loading";
import { getBookingEndTimeAsString } from "../../../../../../utils/singleBooking";
import { EDSBadge } from "../../../../../eds/EDSBadge";
import { Button } from "../../../../../Shared/Buttons/Button";
import DetachedModal from "../../../../../Shared/Modals/DetachedModal/DetachedModal";
import { AreaTabData, getAreaTabData, ZoneData } from "./FindAreasController";
import FindAreasTabContent from "./FindAreasTabContent";

type SelectedTabType = "FreeAreasTab" | "AllAreasTab";

type EmptyStateProps = {
  title: string;
  message: string;
};

const EmptyAreaState = ({ title, message }: EmptyStateProps) => (
  <div className="flex flex-col items-center justify-center h-full min-h-[25vh] text-center p-8">
    <MapPin
      className="h-12 w-12 text-muted-foreground mb-6 opacity-50"
      aria-hidden="true"
    />
    <h2 className="text-xl font-semibold mb-3">{title}</h2>
    <p className="text-muted-foreground text-sm max-w-md">{message}</p>
  </div>
);

type AreaTabContentProps = {
  areaCount: number;
  zones: ZoneData[];
  selected: BookableArea[];
  setSelected: (selected: BookableArea[]) => void;
  emptyTitle: string;
  emptyMessage: string;
  multiSelect?: boolean;
  bookableAreas?: BookableArea[];
};

const AreaTabContent = ({
  areaCount,
  zones,
  selected,
  setSelected,
  emptyTitle,
  emptyMessage,
  multiSelect,
  bookableAreas
}: AreaTabContentProps) => {
  if (areaCount === 0) {
    return <EmptyAreaState title={emptyTitle} message={emptyMessage} />;
  }

  return (
    <FindAreasTabContent
      zones={zones}
      selected={selected}
      setSelected={setSelected}
      multiSelect={multiSelect}
      bookableAreas={bookableAreas}
    />
  );
};
type FindAreasType = {
  open: boolean;
  booking: ViewBooking;
  selectedAreas: BookableArea[];
  setSelectedAreas: (selectedAreas: BookableArea[]) => Promise<void> | void;
  updateBooking?: (booking: Partial<ViewBooking>) => void;
  handleClose: () => void;
  handleWaitlistResponse?: (waitlistSlot: WaitlistSlot) => void;
  onWaitlistAdded?: () => void;
};
const FindAreasModal = ({
  open,
  booking,
  selectedAreas,
  setSelectedAreas,
  updateBooking,
  handleClose,
  handleWaitlistResponse,
  onWaitlistAdded
}: FindAreasType) => {
  const [selected, setSelected] = useState<BookableArea[]>(selectedAreas);
  const [selectedTab, setSelectedTab] =
    useState<SelectedTabType>("FreeAreasTab");
  const [areaTabData, setAreaTabData] = useState<AreaTabData>();
  const bookingContext = useDiaryContext();
  const { connectionStatus } = useDeviceContext();
  const [disableConfirm, setDisableConfirm] = useState<boolean>(false);

  useEffect(() => {
    setDisableConfirm(false);
  }, [open]);

  useEffect(() => {
    setSelected(selectedAreas);
  }, [selectedAreas]);

  const bookingType =
    bookingContext.venueConfig?.booking_types && booking.type
      ? bookingContext.venueConfig.booking_types.find(
          (bookingType) => bookingType.id === booking?.type?.id
        )
      : null;

  const untilTime = getBookingEndTimeAsString(booking);
  const duration = booking.duration || 0;
  const Hours = Math.floor(duration / 60);
  const minutes = duration % 60;

  const selectedDate = new Date(booking.date ?? "");
  const selectedDuration = booking.duration ?? 0;
  const selectedNumPeople = booking.numPeople ?? 1;
  const selectedTime = booking.time ?? "00:00";
  const selectedTypeId = booking.type?.id ?? "";
  const venueId = bookingContext.venueConfig?._id ?? "";

  const {
    data: areaAvailability,
    isLoading,
    error
  } = useQuery({
    queryKey: [
      "AreaAvailability",
      selectedDate,
      selectedDuration,
      selectedNumPeople,
      selectedTime,
      selectedTypeId,
      venueId,
      JSON.stringify(selectedAreas)
    ],
    queryFn: () => {
      if (
        !selectedDate ||
        !selectedDuration ||
        !selectedNumPeople ||
        !selectedTime ||
        !selectedTypeId ||
        !venueId
      ) {
        return null;
      }
      return getAreaAvailability({
        selectedDate,
        selectedDuration,
        selectedNumPeople,
        selectedTime,
        selectedType: selectedTypeId,
        venueId
      });
    },
    gcTime: 0
  });
  const loading = isLoading && connectionStatus === "internet" && !error;

  useEffect(() => {
    if (!error && connectionStatus !== "none") {
      return;
    }

    setAreaTabData(
      getAreaTabData({
        areaAvailability: {
          requested: [],
          earlier: [],
          later: [],
          waitlistSlot: { minutes_until_available: 0 }
        },
        bookingContext,
        bookingTime: booking.time ?? "",
        bookingEndTime: untilTime
      })
    );
  }, [error, connectionStatus]);

  useEffect(() => {
    if (!areaAvailability || !booking.time) {
      return;
    }

    if (handleWaitlistResponse) {
      handleWaitlistResponse(areaAvailability.waitlistSlot);
    }

    setAreaTabData(
      getAreaTabData({
        areaAvailability,
        bookingContext,
        bookingTime: booking.time,
        bookingEndTime: untilTime
      })
    );
  }, [areaAvailability]);

  const onConfirm = async () => {
    setDisableConfirm(true);
    await setSelectedAreas(selected ?? selectedAreas);
    handleClose();
  };

  if (!open) {
    return null;
  }

  const waitlistSlot =
    areaAvailability?.waitlistSlot?.minutes_until_available ?? 0;

  // Format minutes into human-readable time string
  const formatWaitlistTime = (minutes: number): string => {
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const remainingMins = minutes % 60;
      if (remainingMins === 0) {
        return hours === 1 ? "1 hour" : `${hours} hours`;
      }
      const hourText = hours === 1 ? "1 hour" : `${hours} hours`;
      const minText = remainingMins === 1 ? "1 min" : `${remainingMins} mins`;
      return `${hourText} ${minText}`;
    }
    return minutes === 1 ? "1 min" : `${minutes} mins`;
  };

  // Check if booking date is today - aligned with collins-ui logic
  const isBookingDateToday = (): boolean => {
    const bookingDate = booking.date ? new Date(booking.date) : null;
    if (!bookingDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const bookingDateOnly = new Date(bookingDate);
    bookingDateOnly.setHours(0, 0, 0, 0);

    return bookingDateOnly.getTime() === today.getTime();
  };

  // Add to waitlist button visibility - aligned with collins-ui logic:
  // For advanced waitlist venues: only show when date is TODAY, no requested areas available
  // Button is disabled (not hidden) when wait time is invalid
  const advancedWaitlistEnabled =
    bookingContext.venueConfig?.advanced_waitlist_enabled ?? false;
  const maxWaitlistMinutes = 180;
  const noRequestedAreasAvailable =
    (areaAvailability?.requested?.length ?? 0) === 0;
  const showAddToWaitlist =
    advancedWaitlistEnabled &&
    isBookingDateToday() &&
    selectedTab === "FreeAreasTab" &&
    noRequestedAreasAvailable &&
    !booking.waitlisted;
  const waitlistButtonDisabled =
    !booking.numPeople || waitlistSlot < 0 || waitlistSlot > maxWaitlistMinutes;

  return (
    <DetachedModal
      handleClose={() => {
        setSelected(selectedAreas);
        handleClose();
      }}
      heading="Find Areas"
      width="40rem" // changed to be wider as in the design
    >
      <>
        <div className="booking-info-row flex items-center gap-3 mt-3 flex-wrap text-sm text-muted-foreground">
          <span className="time-range text-sm text-foreground">
            {`${booking.time} - ${untilTime}`}
            <span className="duration text-muted-foreground font-normal">{` (${Hours}hrs ${minutes} mins)`}</span>
          </span>
          <EDSBadge
            variant="default"
            className="booking-type-badge"
            style={
              {
                "--booking-type-color":
                  bookingType?.color ?? "hsl(var(--primary))",
                "--booking-type-foreground": bookingType?.color
                  ? "white"
                  : "hsl(var(--primary-foreground))"
              } as React.CSSProperties
            }
          >
            <span className="booking-type-badge-text">
              {bookingType?.name ?? ""}
            </span>
          </EDSBadge>
          <span className="party-size inline-flex items-center gap-1 text-foreground font-semibold">
            <User
              className="AreaUserIcon w-4 h-4 object-contain shrink-0 text-muted-foreground"
              size={14}
              aria-hidden="true"
            />
            {booking.numPeople ?? ""}
          </span>
        </div>

        <div className="FindTableTab pt-2 h-full">
          <div
            className="FindAreasTabs flex border-b border-border gap-1 px-6"
            role="tablist"
          >
            <button
              type="button"
              role="tab"
              aria-selected={selectedTab === "FreeAreasTab"}
              aria-controls="free-areas-tabpanel"
              id="free-areas-tab"
              className={`FindAreasTab flex-1 py-3.5 px-4 border-none bg-transparent text-sm font-medium cursor-pointer relative transition-all duration-150 text-center ${
                selectedTab === "FreeAreasTab"
                  ? "active text-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              onClick={() => setSelectedTab("FreeAreasTab")}
            >
              Free Areas ({areaTabData?.freeAreas.areaCount ?? ""})
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={selectedTab === "AllAreasTab"}
              aria-controls="all-areas-tabpanel"
              id="all-areas-tab"
              className={`FindAreasTab flex-1 py-3.5 px-4 border-none bg-transparent text-sm font-medium cursor-pointer relative transition-all duration-150 text-center ${
                selectedTab === "AllAreasTab"
                  ? "active text-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              onClick={() => setSelectedTab("AllAreasTab")}
            >
              All Areas ({areaTabData?.allAreas.areaCount ?? ""})
            </button>
          </div>
          <div
            className="TagTabContent flex flex-col flex-1"
            style={{ height: "calc(100% - 44px)" }}
          >
            <div className="FindAreas_Content p-0 flex-1 overflow-y-auto overflow-x-hidden flex flex-col min-h-0 max-h-[400px]">
              {loading || !areaTabData ? (
                <Loading />
              ) : (
                <>
                  <div
                    role="tabpanel"
                    id="free-areas-tabpanel"
                    aria-labelledby="free-areas-tab"
                    hidden={selectedTab !== "FreeAreasTab"}
                  >
                    {selectedTab === "FreeAreasTab" && (
                      <AreaTabContent
                        areaCount={areaTabData?.freeAreas.areaCount ?? 0}
                        zones={areaTabData?.freeAreas.zones ?? []}
                        selected={selected ?? selectedAreas}
                        setSelected={setSelected}
                        emptyTitle={
                          connectionStatus === "internet"
                            ? "No Areas Available"
                            : "Connection Issue"
                        }
                        emptyMessage={
                          connectionStatus === "internet"
                            ? "No free areas are available for the selected time and criteria."
                            : "Please check your internet connection and try again."
                        }
                        bookableAreas={
                          bookingContext.venueConfig?.bookable_areas
                        }
                      />
                    )}
                  </div>
                  <div
                    role="tabpanel"
                    id="all-areas-tabpanel"
                    aria-labelledby="all-areas-tab"
                    hidden={selectedTab !== "AllAreasTab"}
                  >
                    {selectedTab === "AllAreasTab" && (
                      <AreaTabContent
                        areaCount={areaTabData?.allAreas.areaCount ?? 0}
                        zones={areaTabData?.allAreas.zones ?? []}
                        selected={selected ?? selectedAreas}
                        setSelected={setSelected}
                        emptyTitle="No Areas Available"
                        emptyMessage="No areas are available for the selected time and criteria."
                        multiSelect={true}
                        bookableAreas={
                          bookingContext.venueConfig?.bookable_areas
                        }
                      />
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </>
      <div className="WaitListButtonArea mt-4 pt-4 border-t border-border shrink-0 flex justify-between">
        {showAddToWaitlist && (
          <div className="WaitListButtonContainer flex items-center">
            <Button
              name="AddToWaitlist"
              fill="solid"
              type="default"
              disabled={waitlistButtonDisabled}
              onClick={() => {
                if (!updateBooking) {
                  onConfirm();
                  return;
                }
                updateBooking({ waitlisted: true });
                onConfirm();
                onWaitlistAdded?.();
              }}
            >
              Add to Waitlist
            </Button>
            {waitlistSlot > 0 && waitlistSlot <= maxWaitlistMinutes && (
              <span className="Waitlist_Est ml-2">{`(Est. ${formatWaitlistTime(waitlistSlot)})`}</span>
            )}
          </div>
        )}
        {!showAddToWaitlist && (
          <Button
            disabled={disableConfirm}
            name="Confirm"
            fill="solid"
            type="primary"
            onClick={() => {
              if (!updateBooking) {
                onConfirm();
                return;
              }
              updateBooking({ waitlisted: false });
              onConfirm();
            }}
          >
            Confirm
          </Button>
        )}
      </div>
    </DetachedModal>
  );
};

export default FindAreasModal;
